import { Menu, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  CheckBadgeIcon,
  EllipsisHorizontalIcon,
  ViewfinderCircleIcon,
  BookmarkIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/24/solid";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";
import MyVendorMembershipMenuItems from "../MyVendors/MyVendorMembershipMenuItems";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function MenuItemSpinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin text-gray-400"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ActionMenu({
  provider,
  handleShowDocDetailsClick = null,
  urlLink = null,
  onActionClick = null,
  hideRequestActions = false,
  canManageMyVendors = false,
  myVendorMembership = null,
  onAddToMyVendors = null,
  onRemoveFromMyVendors = null,
  onAddToHidden = null,
  onUnhide = null,
  onCreateList = null,
  onAddToList = null,
  isBookmarked = false,
  isBookmarkPending = false,
  onToggleBookmark = null,
}) {
  const navigate = useNavigate();

  const handleViewDetails = (e) => {
    e.preventDefault();
    if (onActionClick) {
      onActionClick();
    } else if (urlLink) {
      window.location.href = urlLink;
    }
  };

  const stopMenuAction = (handler) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    handler?.(provider);
  };

  return (
    <Menu as="div" className="relative ml-auto">
      <Menu.Button className="-m-2.5 block p-2.5 text-gray-400 hover:text-gray-500 focus:outline-none">
        <span className="sr-only">Open options</span>
        <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-0.5 w-52 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
          <Menu.Item className="flex items-center">
            {({ active }) => (
              <button
                onClick={handleViewDetails}
                className={classNames(
                  active ? "bg-gray-50" : "",
                  "flex w-full cursor-pointer items-center px-3 py-1 text-left text-sm leading-6 text-gray-900"
                )}
              >
                <ViewfinderCircleIcon className="h-4 w-4" />
                <span className="pl-2" onClick={handleShowDocDetailsClick}>
                  View Details
                </span>
              </button>
            )}
          </Menu.Item>
          {!hideRequestActions && (
            <Menu.Item className="flex items-center">
              {({ active }) => (
                <button
                  onClick={() => navigate(`/RFI/Create?id=${provider.id}`)}
                  className={classNames(
                    active ? "bg-gray-50" : "",
                    "block cursor-pointer px-3 py-1 text-sm leading-6 text-gray-900"
                  )}
                >
                  <CheckCircleIcon className="h-4 w-4 text-indigo-700" />
                  <span className="pl-2">Request VQQ</span>
                  <span className="sr-only">Request VQQ</span>
                </button>
              )}
            </Menu.Item>
          )}
          {!hideRequestActions && (
            <Menu.Item className="flex items-center">
              {({ active }) => (
                <button
                  onClick={() => navigate(`/Assessment/Create?id=${provider.id}`)}
                  className={classNames(
                    active ? "bg-gray-50" : "",
                    "block cursor-pointer px-3 py-1 text-sm leading-6 text-gray-900"
                  )}
                >
                  <CheckBadgeIcon className="h-4 w-4 text-rose-700" />
                  <span className="pl-2">Request VQA</span>
                  <span className="sr-only">Request VQA</span>
                </button>
              )}
            </Menu.Item>
          )}
          {canManageMyVendors && onCreateList && provider ? (
            <Menu.Item className="flex items-center">
              {({ active }) => (
                <button
                  type="button"
                  onClick={stopMenuAction(onCreateList)}
                  className={classNames(
                    active ? "bg-gray-50" : "",
                    "flex w-full cursor-pointer items-center px-3 py-1 text-sm leading-6 text-gray-900"
                  )}
                >
                  <PlusIcon className="h-4 w-4" />
                  <span className="pl-2">Create List</span>
                </button>
              )}
            </Menu.Item>
          ) : null}
          {canManageMyVendors && onAddToList && provider ? (
            <Menu.Item className="flex items-center">
              {({ active }) => (
                <button
                  type="button"
                  onClick={stopMenuAction(onAddToList)}
                  className={classNames(
                    active ? "bg-gray-50" : "",
                    "flex w-full cursor-pointer items-center px-3 py-1 text-sm leading-6 text-gray-900"
                  )}
                >
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  <span className="pl-2">Add to List</span>
                </button>
              )}
            </Menu.Item>
          ) : null}
          {canManageMyVendors && myVendorMembership ? (
            <MyVendorMembershipMenuItems
              vendor={provider}
              isInMyVendors={myVendorMembership.isInMyVendors}
              isManual={myVendorMembership.isManual}
              isHidden={myVendorMembership.isHidden}
              onAddToMyVendors={onAddToMyVendors}
              onRemoveFromMyVendors={onRemoveFromMyVendors}
              onAddToHidden={onAddToHidden}
              onUnhide={onUnhide}
            />
          ) : null}
          {canManageMyVendors && onToggleBookmark && provider ? (
            <Menu.Item disabled={isBookmarkPending} className="flex items-center">
              {({ active }) => (
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onToggleBookmark(provider);
                  }}
                  disabled={isBookmarkPending}
                  className={classNames(
                    active ? "bg-gray-50" : "",
                    "flex w-full cursor-pointer items-center px-3 py-1 text-sm leading-6 text-gray-900 disabled:cursor-wait disabled:opacity-60"
                  )}
                >
                  {isBookmarkPending ? (
                    <MenuItemSpinner />
                  ) : isBookmarked ? (
                    <BookmarkIconSolid className="h-4 w-4 text-indigo-600" />
                  ) : (
                    <BookmarkIcon className="h-4 w-4" />
                  )}
                  <span className="pl-2">
                    {isBookmarkPending
                      ? "Updating…"
                      : isBookmarked
                        ? "Unbookmark"
                        : "Bookmark"}
                  </span>
                </button>
              )}
            </Menu.Item>
          ) : null}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default ActionMenu;
